'use client';

import { SvgAiSpark, SvgBookmarkFill, SvgGovFill } from '@common/icon';
import { cn } from '@core/utils';
import { type MouseEvent,useEffect, useState } from 'react';

import { BOOKMARK_TYPE } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import { ROUTES } from '@/constants';
import { useBookmark } from '@/hooks/useBookmark';

export interface PolicyData {
  id: string;
  contentId?: number;
  title: string;
  aiSummary: string;
  applicationPeriod: string;
  agencyName: string;
  agencyLogo: string;
  isBookmarked: boolean;
}

interface SearchPolicyCardProps {
  policy: PolicyData;
  showBookmark?: boolean;
}

export function SearchPolicyCard({
  policy,
  showBookmark = true,
}: SearchPolicyCardProps) {
  const { bookmark } = useBookmark();
  const [isBookmarked, setIsBookmarked] = useState(policy.isBookmarked);

  useEffect(() => {
    setIsBookmarked(policy.isBookmarked);
  }, [policy.isBookmarked]);

  const handleBookmarkClick = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!policy.contentId) return;

    const wasBookmarked = isBookmarked;
    setIsBookmarked(!wasBookmarked);
    bookmark({
      contentId: policy.contentId,
      isBookmarked: wasBookmarked,
      bookmarkType: BOOKMARK_TYPE.POLICY,
      idx: policy.id,
      contentName: policy.title,
      policyAgency: policy.agencyName,
    });
  };

  return (
    <CustomLink
      href={ROUTES.POLICY_DETAIL.pathname({
        idIdx: policy.id,
      })}
      className="flex cursor-pointer flex-col gap-8 overflow-hidden rounded-medium border border-Gray-100 bg-Gray-white px-24 py-20 transition-colors hover:bg-Gray-25 mobile-tablet:rounded-none mobile-tablet:border-x-0 mobile-tablet:border-b-0 mobile-tablet:border-t mobile-tablet:first:border-t-0"
    >
      <div className="flex h-48 items-start justify-between mobile-tablet:h-auto">
        <h3 className="line-clamp-2 flex-1 overflow-hidden font-600 text-Gray-black typo-body3 mobile-tablet:line-clamp-none mobile-tablet:truncate">
          {policy.title}
        </h3>
        {showBookmark && (
          <button
            type="button"
            aria-label={isBookmarked ? '북마크 해제' : '북마크'}
            className="group size-24 shrink-0 flex-center mobile-tablet:hidden"
            onClick={handleBookmarkClick}
          >
            <SvgBookmarkFill
              className={cn(
                'size-16 text-Gray-400 transition-colors duration-150',
                {
                  'text-B-500': isBookmarked,
                  'group-hover:text-Gray-600': !isBookmarked,
                },
              )}
            />
          </button>
        )}
      </div>

      <div className="text-clip rounded-small bg-Gray-25 p-12">
        <p className="line-clamp-2 text-Gray-600 typo-caption1">
          <span className="mr-4 inline-flex items-center gap-2 align-middle">
            <SvgAiSpark className="size-12" />
            <span className="whitespace-nowrap bg-gradient-to-r from-B-400 to-P-550 bg-clip-text font-700 text-transparent">
              AI 요약
            </span>
          </span>
          {policy.aiSummary}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-x-12 gap-y-4">
        <span className="shrink-0 text-Gray-900 typo-caption1">
          신청기간 : {policy.applicationPeriod}
        </span>
        <div className="ml-auto flex min-w-0 shrink items-center gap-4">
          <div className="rounded-full size-20 overflow-hidden border border-Gray-300 bg-Gray-white flex-center">
            {policy.agencyLogo ? (
              <Image
                src={policy.agencyLogo}
                alt={policy.agencyName}
                width={20}
                height={20}
                className="size-full object-contain"
              />
            ) : (
              <SvgGovFill className="size-12 text-Gray-400" />
            )}
          </div>
          <span className="truncate font-600 text-Gray-500 typo-caption1">
            {policy.agencyName}
          </span>
        </div>
      </div>
    </CustomLink>
  );
}
